import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Theme, Typography, useMediaQuery } from '@mui/material';
import { useSnackbar } from 'notistack';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import {
  PLAN_CODE_MONTHLY,
  PLAN_CODE_TRIAL,
  SUCCESS_OPERATION_ACTION_TYPE_SUBSCRIPTION_CHANGE,
} from 'constants/payment';
import { ISubscriptionContext } from 'context';
import { BackButton, DiscountInput, Info, SeatsCountSelector, Spinner } from 'components';
import {
  ApiUser,
  ComputeSubscriptionProrationMode,
  ComputeSubscriptionProrationResponse,
  Coupon,
  SubscriptionProrationApi,
  UpdateSubscriptionWithIncreasingAmountOfLicensesResponse,
} from 'types';
import { IApiRequests, useApiRequests, useSubscription } from 'hooks';
import { selectUser } from 'store/user';
import { useAppSelector } from 'store';
import { getIsAdminByRoles } from 'utils';
import { getIntlLocale } from 'utils';

import styles from './ManageSeatsPage.module.scss';

type ManageSeatsState = 'minLimitReached' | 'seatsDecrease' | 'notChanged' | 'seatsIncrease';

export function ManageSeatsPage() {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const { t, i18n } = useTranslation('settings');
  const navigate = useNavigate();
  const {
    updateSubscriptionWithDecreasingAmountOfLicenses,
    updateSubscriptionWithIncreasingAmountOfLicenses,
    computeSubscriptionProration,
  }: IApiRequests = useApiRequests();
  const subscription: ISubscriptionContext | null = useSubscription();

  const user: ApiUser = useAppSelector(selectUser)!;

  const [loading, setLoading] = useState<boolean>(false);
  const [seatsCount, setSeatsCount] = useState<number>(subscription?.totalSeats ?? 0);
  const [coupone, setCoupone] = useState<Coupon | null>(null);
  const [subscriptionProration, setSubscriptionProration] = useState<SubscriptionProrationApi | null>(null);

  const locale = getIntlLocale(i18n.resolvedLanguage || i18n.language || 'en');

  const formatLocalizedDate = (value?: string | null, options?: Intl.DateTimeFormatOptions): string => {
    if (!value) {
      return '--';
    }

    return new Intl.DateTimeFormat(locale, options ?? { month: 'short', year: 'numeric' }).format(new Date(value));
  };

  const manageSeatsState: ManageSeatsState = useMemo(() => {
    if (!subscription) {
      return 'notChanged';
    }
    if (seatsCount < subscription.totalSeats - subscription.availableSeats) {
      return 'minLimitReached';
    }

    if (seatsCount < subscription.totalSeats) {
      return 'seatsDecrease';
    }

    if (seatsCount > subscription.totalSeats) {
      return 'seatsIncrease';
    }

    return 'notChanged';
  }, [subscription, seatsCount]);

  const pricePerOneSeatPerMonth: string = useMemo(() => {
    if (user.clinicUser.subscriptionInfo?.planCode === PLAN_CODE_MONTHLY) {
      return user.clinicUser.subscriptionInfo!.price?.toFixed(2) ?? '';
    }

    return ((user.clinicUser.subscriptionInfo?.price ?? 0) / 12).toFixed(2);
  }, [user]);

  const cardExparationString: string = useMemo(() => {
    const year = user.clinicUser.subscriptionInfo?.cardExpirationYear ?? 2022;
    const month = user.clinicUser.subscriptionInfo?.cardExpirationMonth ?? 1;
    return new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' }).format(new Date(year, month - 1, 1));
  }, [locale, user]);

  const currentTotalPrice: string = useMemo(() => {
    return ((user.clinicUser.subscriptionInfo?.price ?? 0) * (subscription?.totalSeats ?? 0)).toFixed(2);
  }, [subscription, user]);

  const renewsDate: string = useMemo(() => {
    return t('subscriptionPages.renews', {
      date: formatLocalizedDate(user.clinicUser.subscriptionInfo?.currentTermEndsAt, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }),
    });
  }, [t, user, locale]);

  useEffect(() => {
    if (subscription) {
      setSeatsCount(subscription.totalSeats);
    }
  }, [subscription]);

  useEffect(() => {
    if (
      !isMobile ||
      user.clinicUser?.subscriptionInfo?.planCode === PLAN_CODE_TRIAL ||
      !getIsAdminByRoles(user.clinicUser.roles)
    ) {
      navigate('/');
    }
  }, [isMobile, navigate, user.clinicUser?.subscriptionInfo?.planCode, user.clinicUser.roles]);

  useEffect(() => {
    if (seatsCount) {
      setLoading(true);

      computeSubscriptionProration({
        plan: { code: user.clinicUser.subscriptionInfo!.planCode!, quantity: seatsCount },
        mode: ComputeSubscriptionProrationMode.ProrateExistingSubscription,
        couponCode: coupone?.code,
      })
        .then((response: ComputeSubscriptionProrationResponse) => {
          setSubscriptionProration(response.subscriptionProration);
        })
        .catch((error) => {
          enqueueSnackbar(t('subscriptionNotifications.calculationError', { message: error.message }), {
            variant: 'error',
          });
          setSubscriptionProration(null);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [computeSubscriptionProration, coupone, enqueueSnackbar, seatsCount, t, user]);

  const handleBackToSubscriptionsClick = (): void => {
    navigate('/settings#subscriptions');
  };

  const handleDecreaseSeat = (): void => {
    if (seatsCount > 1) {
      setSeatsCount(seatsCount - 1);
    }
  };

  const handleIncreaseSeat = (): void => {
    setSeatsCount(seatsCount + 1);
  };

  const handleIncrease = (): void => {
    if (coupone?.code === 'LIMITED50' && seatsCount < 10) {
      enqueueSnackbar(t('subscriptionNotifications.limited50MinSeatsError'), {
        variant: 'error',
      });
      return;
    }

    setLoading(true);

    updateSubscriptionWithIncreasingAmountOfLicenses({
      planCode: user.clinicUser.subscriptionInfo!.planCode!,
      quantity: seatsCount,
      redirectUrl: `${process.env.REACT_APP_PUBLIC_URI}/successPayment?type=${SUCCESS_OPERATION_ACTION_TYPE_SUBSCRIPTION_CHANGE}&redirectTo=settings&redirectToHash=subscriptions`,
      couponCode: coupone?.code,
    })
      .then((response: UpdateSubscriptionWithIncreasingAmountOfLicensesResponse) => {
        window.location.href = response.page.url;
      })
      .catch((error) => {
        enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDecrease = (): void => {
    setLoading(true);

    updateSubscriptionWithDecreasingAmountOfLicenses({
      planCode: user.clinicUser.subscriptionInfo!.planCode!,
      quantity: seatsCount,
    })
      .then(() => {
        window.location.href = `${process.env.REACT_APP_PUBLIC_URI}/successPayment?type=${SUCCESS_OPERATION_ACTION_TYPE_SUBSCRIPTION_CHANGE}&redirectTo=settings&redirectToHash=subscriptions`;
      })
      .catch((error) => {
        enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleApplyCoupon = (coupon: Coupon): void => {
    setCoupone(coupon);
  };

  const handleRemoveCoupon = (): void => {
    setCoupone(null);
  };

  const handleManageStaff = (): void => {
    navigate('/settings#staff');
  };

  const handleToAnnual = async (): Promise<void> => {
    navigate('/upgradeToAnnualPlan');
  };

  const handleBack = (): void => {
    navigate('/settings#subscriptions');
  };

  return (
    <>
      {loading && <Spinner />}
      <div className={styles.page}>
        <BackButton text={t('subscriptionPages.backToSubscription')} onClick={handleBackToSubscriptionsClick} />

        <div className={styles.container}>
          <Box className={styles.containerTop}>
            <Typography variant='body4' component='span'>
              {t('subscriptionPages.currentQuantity')}
            </Typography>

            <div className={classnames(styles.row, styles.pricePerMonth)}>
              <Typography variant='h4' component='h4'>
                {t('subscriptionPages.monthSeatRate', { price: pricePerOneSeatPerMonth })}
              </Typography>

              <Info text={t('subscriptionPages.seatsInfo')} tooltipClassName={styles.tooltip} />

              <Typography variant='body5' component='span' color='#626363'>
                {user.clinicUser.subscriptionInfo?.planCode === PLAN_CODE_MONTHLY
                  ? t('subscriptionPages.billedMonthly')
                  : t('subscriptionPages.billedPerSeatAnnually', {
                      amount: (user.clinicUser.subscriptionInfo?.price ?? 0).toFixed(2),
                    })}
              </Typography>
            </div>

            <div className={styles.devider}></div>

            <div className={styles.row}>
              <div className={styles.calculationsRow}>
                <Typography variant='body5' component='span' color='#393534'>
                  {t('subscriptionPages.totalSeats')}
                </Typography>

                <Typography variant='body5' component='span' color='#393534'>
                  {subscription?.totalSeats}
                </Typography>
              </div>

              <div className={styles.calculationsRow}>
                <Typography variant='body4' component='span'>
                  {t('subscriptionPages.total')}
                </Typography>

                <Typography variant='body4' component='span'>
                  ${currentTotalPrice}
                </Typography>
              </div>
            </div>

            <div className={styles.devider}></div>

            <div className={styles.row}>
              <Typography variant='body2' component='span'>
                {t('subscriptionPages.paymentMethod')}
              </Typography>

              <div className={styles.card}>
                <Typography variant='body3' component='span' color='#393534'>
                  {t('subscriptionPages.cardEndingIn', {
                    digits: user.clinicUser.subscriptionInfo?.cardLastFourDigits ?? '0000',
                  })}
                </Typography>

                <Typography variant='body5' component='span' color='#393534'>
                  {t('subscriptionPages.expires', { date: cardExparationString })}
                </Typography>
              </div>
            </div>

            {user.clinicUser.subscriptionInfo?.planCode === PLAN_CODE_MONTHLY && (
              <div className={styles.toAnnual} onClick={handleToAnnual}>
                <Typography variant='body5' component='span'>
                  <u>{t('subscriptionPages.saveByUpgradingAnnual')}</u>
                </Typography>
              </div>
            )}
          </Box>

          <Box className={styles.containerBottom}>
            <Typography component='p' variant='body4'>
              {t('subscriptionPages.newQuantity')}
            </Typography>

            <div className={styles.row}>
              <Typography variant='body2' component='span'>
                {t('subscriptionPages.totalSeats')}
              </Typography>

              <SeatsCountSelector
                count={seatsCount}
                decreaseDisabled={manageSeatsState === 'minLimitReached' || seatsCount === 1}
                onDecrease={handleDecreaseSeat}
                onIncrease={handleIncreaseSeat}
              />
            </div>

            <div className={styles.row}>
              <Typography variant='body2' component='span'>
                {t('subscriptionPages.discountCode')}
              </Typography>

              <DiscountInput
                onApply={handleApplyCoupon}
                onRemove={handleRemoveCoupon}
                plan={user.clinicUser.subscriptionInfo!.planCode!}
                disabled={manageSeatsState !== 'seatsIncrease'}
              />
            </div>

            <div className={styles.devider}></div>

            <div className={styles.row}>
              <div className={styles.calculationsRow}>
                <Typography variant='body5' component='span' color='#393534'>
                  {t('subscriptionPages.discount')}
                </Typography>

                <Typography variant='body5' component='span' color='#393534'>
                  -${subscriptionProration?.initialDiscount.toFixed(2)}
                </Typography>
              </div>

              {manageSeatsState !== 'seatsIncrease' && (
                <div className={styles.calculationsRow}>
                  <Typography variant='body4' component='span'>
                    {manageSeatsState === 'notChanged'
                      ? t('subscriptionPages.currentTotal')
                      : t('subscriptionPages.newTotal')}
                  </Typography>

                  <Typography variant='body4' component='span'>
                    ${subscriptionProration?.recurringChargeTotal.toFixed(2)}
                  </Typography>
                </div>
              )}

              {manageSeatsState === 'seatsIncrease' && (
                <>
                  <div className={styles.calculationsRow}>
                    <Typography variant='body5' component='span' color='#393534'>
                      {t('subscriptionPages.newTotal')}
                    </Typography>

                    <Typography variant='body5' component='span' color='#393534'>
                      ${subscriptionProration?.recurringChargeTotal.toFixed(2)}
                    </Typography>
                  </div>

                  <div className={styles.calculationsRow}>
                    <Typography variant='body4' component='span'>
                      {t('subscriptionPages.proratedAmountDue')}
                    </Typography>

                    <Typography variant='body4' component='span'>
                      ${subscriptionProration?.initialChargeAmount.toFixed(2)}
                    </Typography>
                  </div>
                </>
              )}
            </div>

            {manageSeatsState === 'minLimitReached' && (
              <Typography color='#FF3D3D' variant='body1' component='span'>
                {t('subscriptionPages.minSeatsReachedLine1')}
                <br />
                {t('subscriptionPages.minSeatsReachedLine2')}
              </Typography>
            )}

            {manageSeatsState === 'seatsDecrease' && (
              <Typography color='#626363' variant='body1' component='span'>
                {t('subscriptionPages.seatsDecreaseInfo')}
              </Typography>
            )}

            {manageSeatsState === 'seatsIncrease' && (
              <Typography color='#626363' variant='body1' component='span'>
                {t('subscriptionPages.seatsIncreaseInfo')}
              </Typography>
            )}

            <div className={styles.devider}></div>

            <div>
              <Typography variant='body5' component='span' color='#626363'>
                {renewsDate}
              </Typography>
            </div>

            <div className={styles.buttons}>
              {manageSeatsState === 'minLimitReached' && (
                <Button className={styles.manageStaff} variant='contained' onClick={handleManageStaff}>
                  {t('subscriptionPages.manageStaff')}
                </Button>
              )}

              <Button variant='outlined' onClick={handleBack}>
                {t('subscriptionPages.back')}
              </Button>

              {manageSeatsState === 'minLimitReached' && (
                <Button variant='contained' disabled>
                  {t('subscriptionPages.update')}
                </Button>
              )}

              {manageSeatsState === 'seatsDecrease' && (
                <Button variant='contained' onClick={handleDecrease}>
                  {t('subscriptionPages.update')}
                </Button>
              )}

              {manageSeatsState === 'notChanged' && (
                <Button variant='contained' disabled>
                  {t('subscriptionPages.payNow')}
                </Button>
              )}

              {manageSeatsState === 'seatsIncrease' && (
                <Button variant='contained' onClick={handleIncrease}>
                  {t('subscriptionPages.payAmount', {
                    amount: subscriptionProration?.initialChargeAmount.toFixed(2),
                  })}
                </Button>
              )}
            </div>
          </Box>
        </div>
      </div>
    </>
  );
}
